import * as React from "react";
import { RouterLink as Link } from "../router";

import { cn } from "../utils";
import { Count } from "../count";
import { Icon } from "../icon";
import { IntentBadge } from "./status-badge";
import type { Intent } from "../tokens";

/**
 * SectionTitle — the ONE way to title a card or a section of content, including
 * data-viz cards.
 *
 * Consolidation target (docs/design/CARD-TITLE-SYSTEM — prototype at
 * /styleguide/card-titles). The app grew ~6 competing title treatments:
 *
 *   - `SectionHeader` (list-card chrome)  → title + Count pill + border-b
 *   - `SectionHeading` (lg/md/sm)         → standalone, no count, no divider
 *   - `CardHeader`+`CardTitle` ad-hoc     → `type-body` + a hand-rolled inline
 *                                           `<span>` count + a bespoke action
 *   - `SuggestionsPanel` header           → textured panel, `type-title`, a
 *                                           marketing subtitle, an "x of y" chip
 *   - chart cards                         → title + a right-aligned muted meta
 *                                           span + ad-hoc verdict pills
 *   - hand-rolled `<h2|h3>` + classes     → scattered across blocks
 *
 * They drift on every axis: title size, how the count renders, whether there's
 * a divider, how the right-aligned action looks, and how a trend/verdict reads.
 * This collapses all of it into the treatment the design language already
 * blesses — the `DataTable` header + the review inbox ("To review · 16 · View
 * all"): a `font-medium` title, the neutral `Count` pill, an optional COLORED
 * "View all" link (primary + arrow, so it reads as a link), and (for a
 * card/list chrome row) a hairline divider on the `px-4` gutter.
 *
 * Two variants, one look:
 *   - `card`  (default) — the chrome row at the top of a `<Card>`/list: hairline
 *     divider + the `px-4 py-3` gutter. This is `SectionHeader` / a `DataTable`
 *     header / the inbox header.
 *   - `plain`           — a bare in-page section heading: no divider, no gutter
 *     (replaces `SectionHeading`).
 *
 * Three tiers on the type scale, all BELOW the PageNav page title
 * (`type-page-title`, 28px) so a section never outsizes its page. All three
 * retained layout variants resolve to the single 16px subsection-title role.
 *
 * Slots, each with ONE representation:
 *   - `count`   → the neutral `Count` pill (never a hand-rolled span / "x of y"
 *     chip / colored bubble — that's `COUNT_BUBBLE`, an attention signal).
 *   - `subtitle`→ one muted line below the title. The home for a chart card's
 *     meta ("$3,414 · 1622 runs · last 30 days") or an explanatory description.
 *   - `trend`   → a directional verdict ("Regressing"/"Improving"/"Flat") as the
 *     ONE verdict representation: an `IntentBadge` with a dot, right-aligned.
 *   - `action` / `viewAllHref` → the right-aligned "go there" link/control.
 */
export type SectionTitleSize = "sm" | "md" | "lg";

const TITLE_SIZE: Record<SectionTitleSize, string> = {
  sm: "type-title",
  md: "type-title",
  lg: "type-title",
};

const ICON_SIZE: Record<SectionTitleSize, "sm" | "base"> = {
  sm: "sm",
  md: "base",
  lg: "base",
};

/** A directional verdict for a data-viz header. The ONE trend representation. */
export interface SectionTitleTrend {
  /** The verdict word, e.g. "Regressing", "Improving", "Flat". */
  label: React.ReactNode;
  /** Design-token intent → the badge color (danger/success/neutral/…). */
  intent: Intent;
}

export interface SectionTitleProps {
  /** The title. A string, or a node (e.g. a status dot + label). */
  title: React.ReactNode;
  /**
   * Optional leading glyph (kebab name, resolved through `<Icon>`), rendered
   * muted at the title's scale. The review-inbox department headers use this.
   */
  icon?: string;
  /**
   * Numeric tally → the neutral `Count` pill, immediately after the title.
   * This is the ONE count style for a title row; don't pass a pre-formatted
   * string or hand-roll a `<span>`.
   */
  count?: number;
  /**
   * One muted context line below the title — real context, not marketing. The
   * home for a chart card's meta line ("$3,414 · 1622 runs · last 30 days") or
   * an explanatory description ("Verified-done work the engine produces").
   */
  subtitle?: React.ReactNode;
  /** Tiny kicker above the title (rare — e.g. "Last 30 days · fleet-wide"). */
  eyebrow?: React.ReactNode;
  /**
   * A data-viz verdict — rendered as the ONE trend representation: an
   * `IntentBadge` with a dot, in the right-aligned cluster (before any action).
   */
  trend?: SectionTitleTrend;
  /**
   * Right-aligned action node (a `<Button>`, a menu). For the canonical "View
   * all"-style link, prefer the `viewAllHref` shortcut so every surface renders
   * the link identically. `action` wins over `viewAllHref` if both are passed.
   */
  action?: React.ReactNode;
  /** Shortcut: render the canonical colored "View all" link (primary + arrow). */
  viewAllHref?: string;
  /** Label for the `viewAllHref` link. Default "View all". */
  viewAllLabel?: string;
  /** Type-scale tier. Default `sm` — the table/inbox treatment. */
  size?: SectionTitleSize;
  /**
   * `card` (default) — chrome row atop a Card/list (divider + `px-4 py-3`).
   * `plain` — a bare in-page section heading (no divider, no gutter).
   */
  variant?: "card" | "plain";
  /** Heading element. Default `h2`. */
  as?: "h2" | "h3" | "h4";
  /** Extra classes on the wrapper row. */
  className?: string;
  /** Extra classes on the heading element itself (e.g. `type-page-title`). */
  titleClassName?: string;
}

/**
 * The single "go there" link treatment — muted at rest, foreground on hover,
 * plus a trailing arrow so an action link still reads as a LINK rather than as
 * body text. Used by every `viewAllHref`.
 *
 * NOT `text-primary` (Matt, 2026-07-22: "we also need to make See all gray/white
 * but not blue"). "See all" is chrome — it sits in a card header on almost every
 * surface in the app, so painting it the brand color spent the one hue that is
 * supposed to mean "this is THE action here" on the most repeated, least
 * important control on screen. Muted-to-foreground keeps it findable, keeps the
 * arrow doing the "this navigates" work, and gives primary back its scarcity.
 */
const VIEW_ALL_LINK =
  "inline-flex items-center gap-1 rounded-sm type-subtext text-muted-foreground underline-offset-4 decoration-muted-foreground/40 transition-colors hover:text-foreground hover:underline";

/** Render the canonical colored action link (primary + trailing arrow). */
export function SectionTitleLink({
  href,
  children = "View all",
  className,
}: {
  href: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <Link href={href} className={cn(VIEW_ALL_LINK, className)}>
      {children}
      <Icon name="arrow-right" size="sm" className="shrink-0" />
    </Link>
  );
}

export function SectionTitle({
  title,
  icon,
  count,
  subtitle,
  eyebrow,
  trend,
  action,
  viewAllHref,
  viewAllLabel = "View all",
  size = "sm",
  variant = "card",
  as: Tag = "h2",
  className,
  titleClassName,
}: SectionTitleProps) {
  const resolvedAction =
    action ??
    (viewAllHref != null ? (
      <SectionTitleLink href={viewAllHref}>{viewAllLabel}</SectionTitleLink>
    ) : null);

  const trendBadge =
    trend != null ? (
      <IntentBadge intent={trend.intent} withDot>
        {trend.label}
      </IntentBadge>
    ) : null;

  const cluster =
    trendBadge != null || resolvedAction != null ? (
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {/* The trend + action slots are adjacent dynamic children; React's dev
            runtime flags them as a keyless list (the warning names this
            component when a caller passes an element `action`). Key each slot
            so the consumer's node never trips the missing-key check. */}
        {[
          trendBadge != null ? (
            <React.Fragment key="trend">{trendBadge}</React.Fragment>
          ) : null,
          resolvedAction != null ? (
            <React.Fragment key="action">{resolvedAction}</React.Fragment>
          ) : null,
        ]}
      </div>
    ) : null;

  return (
    // Column layout: an optional eyebrow, then a title↔action row, then the
    // subtitle FULL-WIDTH beneath. The action rides the title line (so a button
    // sits beside the title, not floating in the middle of a tall description),
    // and a long explanation gets the whole width at a comfortable measure
    // instead of being crushed into a narrow column beside the action.
    <div
      data-slot="section-title"
      className={cn(
        "flex flex-col gap-1",
        // px-4 matches the table atoms' flat 16px cell inset (table.tsx) so an
        // integrated card header shares its table's first-column left edge.
        // `bg-foreground/[0.04]` — the header is a CHROME row sitting above
        // content, and the wash is what makes it read as one (the same tint
        // `WorkGroup`/`CardStrip` use). Centralised here so every titled card —
        // ListCard, WorkGroup, a chart card — wears the same header strip
        // instead of some tinting and some not (Matt, 2026-07-23: "the titlebar
        // doesn't have the background color").
        variant === "card" &&
          "border-b border-border bg-foreground/[0.04] px-[var(--content-gutter)] py-[var(--chrome-py)]",
        className,
      )}
    >
      {eyebrow != null ? (
        <span className="type-subtext text-muted-foreground">{eyebrow}</span>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {icon != null ? (
            <Icon
              name={icon}
              size={ICON_SIZE[size]}
              className="shrink-0 text-muted-foreground"
            />
          ) : null}
          <Tag
            className={cn(TITLE_SIZE[size], "text-foreground", titleClassName)}
          >
            {title}
          </Tag>
          {typeof count === "number" ? <Count value={count} /> : null}
        </div>
        {cluster}
      </div>
      {subtitle != null ? (
        // A card chrome row keeps the subtitle dense (type-subtext); a standalone
        // (plain) heading gives it the design-language description size
        // (type-body). `max-w-prose` keeps a long explanation readable.
        <p
          className={cn(
            "max-w-prose text-muted-foreground",
            variant === "card" ? "type-subtext" : "type-body",
          )}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}
